/**
 * Device-local memory for duels (eager, tiny, type-only imports): the duel in flight, so a
 * duel left mid-round (quit, reload, lost connection) still gets its result card on the next
 * map visit, and a rematch asked for on the result screen. Tagged with the player id; every
 * read/write survives a broken store.
 */
import type { AppContext } from '../../game/context.js';
import type { DuelInfo, DuelMemory, DuelOver } from './session.js';

type Ctx = Pick<AppContext, 'services'>;

const PENDING = 'feature.duels.pending';
/** A duel older than this is settled for sure (apps/realtime forfeits at 10 min); forget it. */
const STALE_MS = 60 * 60_000;

export interface PendingDuel {
  duelId: string;
  /** `services.now()` when matched */
  at: number;
  opp: { name: string; bot: boolean };
  /** this device submitted the round or left the duel: never play it again (the server re-sends a running duel after a reload) */
  over?: DuelOver;
}

const me = (a: Ctx): string | null => a.services.store.state.auth?.playerId ?? null;

export function pendingDuel(a: Ctx): PendingDuel | null {
  try {
    const raw = a.services.kv.get(PENDING);
    if (!raw) return null;
    const box = JSON.parse(raw) as { p?: string | null; v?: PendingDuel };
    const v = box.v;
    if (box.p !== me(a) || !v || typeof v.duelId !== 'string') return null;
    return a.services.now() - v.at > STALE_MS ? null : v;
  } catch {
    return null;
  }
}

export function setPendingDuel(a: Ctx, v: PendingDuel | null): void {
  try {
    if (v === null) a.services.kv.remove(PENDING);
    else a.services.kv.set(PENDING, JSON.stringify({ p: me(a), v }));
  } catch {
    // storage full or blocked: the card is a convenience only
  }
}

/** The duel in flight becomes the remembered one (its `over` mark kept when it already is). */
export function rememberDuel(a: Ctx, duel: Pick<DuelInfo, 'duelId' | 'opp'>): void {
  if (pendingDuel(a)?.duelId === duel.duelId) return;
  setPendingDuel(a, { duelId: duel.duelId, at: a.services.now(), opp: { name: duel.opp.name, bot: duel.opp.bot } });
}

/** The session's memory of the duel this device is done with, on the device (survives a reload). */
export function duelMemory(a: Ctx): DuelMemory {
  return {
    over: (duelId) => {
      const p = pendingDuel(a);
      return p?.duelId === duelId ? (p.over ?? null) : null;
    },
    markOver: (duel, how) => {
      rememberDuel(a, duel);
      const p = pendingDuel(a);
      if (p?.duelId === duel.duelId) setPendingDuel(a, { ...p, over: how });
    },
  };
}

/** Forget the duel once its result was shown (only if it is still the one remembered). */
export function clearPendingDuel(a: Ctx, duelId: string): void {
  if (pendingDuel(a)?.duelId === duelId) setPendingDuel(a, null);
}
